<template>
  <div class="h-full">
    <SiteList
      v-show="currentStep === LinkedToStep.SITES"
      showChevron
      :sites="filteredSites"
      @selected="handleStepForward"
      @checked="handleCheck"
    />
    <TypeList
      v-show="currentStep === LinkedToStep.TYPES"
      :checkedTypes="checkedTypes"
      :indeterminateTypes="currentSiteIndeterminateTypes"
      :showCheckbox="checkDescendants"
      :showSiteItem="!!siteId"
      @click="handleStepForward"
      @check="handleCheck"
    />
    <ChildrenList
      v-show="
        currentStep === LinkedToStep.SPACES ||
        currentStep === LinkedToStep.ASSETS
      "
      ref="childrenList"
      showCheckbox
      :children="currentStepChildren"
      :checkedChildren="currentSiteCheckedChildrenIds"
      :indeterminateChildren="currentSiteIndeterminateChildrenIds"
      :chevronChildren="currentSiteParentIds"
      :disableCheck="type === 'single' && activeChildIds.length > 0"
      @click="handleStepForward"
      @check="handleCheck"
    />
    <ChildrenList
      v-show="currentStep === LinkedToStep.SEARCH"
      showCheckbox
      :searchQuery="searchQuery"
      :checkedChildren="currentSiteCheckedChildrenIds"
      :indeterminateChildren="currentSiteIndeterminateChildrenIds"
      :children="searchResults"
      :disableCheck="type === 'single' && activeChildIds.length > 0"
      @check="handleCheck"
    />
  </div>
</template>

<script setup>
import { isString } from "lodash";
import { computed, onMounted, ref, watchEffect } from "vue";
import { LinkedToType, LinkedToStep, useLinkedTo } from "@/Components/LinkedTo";
import { SiteList } from "@/Components/Site";
import TypeList from "@/Components/TypeList.vue";
import ChildrenList from "@/Components/ChildrenList.vue";

const emit = defineEmits(["change", "isNavigating"]);

const childrenList = ref(null);

const props = defineProps({
  /**
   * {
   *   // Arrays of IDs
   *   sites: [],
   *   children: [],
   * }
   */
  initialState: {
    type: Object,
    default: null,
  },
  type: {
    type: String,
    validator: (val) => ["single", "multiple"].includes(val),
    default: "multiple",
  },
  sites: { type: Array, default: () => [] },
  siteId: { type: String, required: false },
  query: { type: String, default: "" },
  filterSitesFn: { type: Function, default: null },
  showSiteCheckboxes: { type: Boolean, default: true },
  checkDescendants: { type: Boolean, default: true },
  filter: { type: Boolean, default: false },
});

const filteredSites = computed(() => {
  const filtered = props.filterSitesFn
    ? props.sites.filter(props.filterSitesFn)
    : props.sites;

  if (!props.siteId) {
    return filtered;
  }

  return filtered.filter((site) => site.id === props.siteId);
});

const {
  currentStep,
  currentStepChildren,
  currentSiteParentIds,
  currentSiteCheckedChildrenIds,
  currentSiteCheckedTypes,
  currentSiteIndeterminateChildrenIds,
  currentSiteIndeterminateTypes,
  checkedSiteIds,
  searchQuery,
  searchResults,
  handleStepForward: _handleStepForward,
  handleStepBack: _handleStepBack,
  handleCheck: _handleCheck,
  handleSearch,
  hasSelectionBeenMade,
  getState,
  getChecked,
} = useLinkedTo(props.initialState, filteredSites, props.checkDescendants);

const checkedTypes = computed(() => {
  if (checkedSiteIds.value.includes(props.siteId)) {
    return [...currentSiteCheckedTypes.value, LinkedToType.SITE];
  }
  return currentSiteCheckedTypes.value;
});

const activeChildIds = computed(() =>
  [
    ...currentSiteCheckedChildrenIds.value,
    ...currentSiteIndeterminateChildrenIds.value,
  ].filter((id) => isString(id))
);

onMounted(() => {
  if (props.siteId) {
    handleStepForward(props.siteId);
    if (props.type === "single") {
      if (activeChildIds.value.length > 0) {
        goToSelection();
      }
    }
  }
});

watchEffect(() => {
  handleSearch(props.query);
});

function handleCheck(event) {
  _handleCheck(event);
  emit("change");
}

function goToSelection() {
  emit("isNavigating");
  _handleStepForward(
    [
      ...currentSiteCheckedTypes.value,
      ...currentSiteIndeterminateTypes.value,
    ].filter((id) => id)[0]
  );

  if (currentSiteIndeterminateChildrenIds.value.length < 1) {
    return;
  }

  let child = currentStepChildren.value.find((child) =>
    activeChildIds.value.includes(child.id)
  );
  for (let i = 1; i < currentSiteIndeterminateChildrenIds.value.length; i++) {
    _handleStepForward(child.id);
    child = currentStepChildren.value.find((child) =>
      activeChildIds.value.includes(child.id)
    );
  }
  if (currentSiteIndeterminateChildrenIds.value.includes(child.id)) {
    handleStepForward(child.id);
  }
}

function handleStepForward(event) {
  emit("isNavigating");
  _handleStepForward(event);
  if (props.type === "single") {
    scrollToSelection(activeChildIds.value);
  }
}

function handleStepBack() {
  emit("isNavigating");
  _handleStepBack();
  if (props.type === "single") {
    scrollToSelection(activeChildIds.value);
  }
}

const scrollToSelection = (value) => {
  if (childrenList.value) {
    setTimeout(() => {
      childrenList.value.scrollToSelection(value);
    }, 10);
  }
};

defineExpose({
  currentStep,
  handleBack: handleStepBack,
  hasSelectionBeenMade,
  getState,
  getChecked,
  scrollToSelection,
});
</script>
